/* =======================================================================
   LinksPage — Pagina nascosta /links (linktree)
   =======================================================================
   Accessibile SOLO da link diretto: tuosito.it/#/links
   Non compare nel menu in alto né in nessun altro link interno del sito.
   Pensata per essere condivisa su biografia Instagram/Facebook/WhatsApp.

   I bottoni puntano a:
     1. Home page del sito         → /#/
     2. Pagina Menù                → /#/menu
     3. Chiamata diretta (fisso)   → tel:+390586790139
     4. Google Maps (indicazioni)  → maps.google.com/?q=...
     5. Pagina Facebook            → facebook.com/lesimpatichecanaglierosignano

   Per modificare un link, cerca i tag <a href=...> qui sotto.
   ======================================================================= */
import { Link } from "react-router-dom";
import { asset } from "@/utils/asset";

const LINKS = [
  {
    label: "Home",
    sub: "Vai al sito",
    href: "/",
    internal: true,
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    label: "Menù",
    sub: "Pizze, schiacciatine, dolci",
    href: "/menu",
    internal: true,
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  {
    label: "Chiamaci",
    sub: "0586 790 139",
    href: "tel:+390586790139",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
  {
    label: "Come arrivare",
    sub: "Indicazioni stradali",
    href: "https://maps.google.com/?q=Via+Antonio+Gramsci+55,+Rosignano+Marittimo,+LI,+Italy",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    label: "Facebook",
    sub: "Eventi e novità",
    href: "https://www.facebook.com/lesimpatichecanaglierosignano",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22 12.07C22 6.5 17.52 2 12 2S2 6.5 2 12.07c0 5.02 3.66 9.19 8.44 9.93v-7.02H7.9v-2.91h2.54V9.84c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.74 8.44-4.91 8.44-9.93z" />
      </svg>
    ),
  },
];

export default function LinksPage() {
  return (
    <div
      data-testid="page-links"
      className="min-h-screen bg-[#2C2C2A] text-[#F7F4EB] flex flex-col items-center px-6 py-16 lg:py-20 relative overflow-hidden"
    >
      {/* texture noise */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='200' height='200' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* warm gradient blob */}
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[480px] h-[480px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, #BC472A 0%, transparent 70%)" }}
      />

      <div className="relative w-full max-w-md flex flex-col items-center">
        {/* Logo with ® */}
        <Link to="/" className="relative inline-block mb-6 transition-transform hover:scale-[1.02]">
          <img
            src={asset("/assets/logo.jpg")}
            alt="Le Simpatiche Canaglie"
            data-testid="links-logo"
            className="h-28 w-auto rounded-sm shadow-2xl"
          />
          <sup className="absolute -top-1 -right-3 text-[0.8rem] text-[#E6C9A8] font-serif font-medium leading-none">®</sup>
        </Link>

        {/* Brand */}
        <div className="text-center mb-2">
          <h1 className="font-serif italic text-3xl sm:text-4xl leading-tight">
            Le Simpatiche <span className="font-handwriting text-[#E6C9A8] not-italic">Canaglie</span>
          </h1>
        </div>
        <p className="text-[0.7rem] tracking-[0.32em] uppercase text-[#E6C9A8]/80 mb-2">
          Rosignano Marittimo · Toscana
        </p>
        <p className="text-sm font-light text-[#EDE6D6]/85 text-center mb-10 max-w-xs">
          Pizzeria artigianale · Pasta madre · Grani antichi · Cucina senza glutine AIC
        </p>

        {/* Buttons */}
        <div className="w-full flex flex-col gap-3">
          {LINKS.map((l, i) =>
            l.internal ? (
              <Link
                key={i}
                to={l.href}
                data-testid={`link-${i}`}
                className="group flex items-center gap-4 bg-[#F7F4EB] hover:bg-[#BC472A] text-[#2C2C2A] hover:text-[#F7F4EB] px-5 py-4 rounded-xl border border-[#F7F4EB]/10 transition-all duration-300 hover:-translate-y-0.5 shadow-md"
              >
                <span className="shrink-0 h-10 w-10 grid place-items-center rounded-lg bg-[#2C2C2A]/8 group-hover:bg-[#F7F4EB]/15 text-[#BC472A] group-hover:text-[#F7F4EB] transition-colors">
                  {l.icon}
                </span>
                <span className="flex-1 text-left">
                  <span className="block font-serif text-lg leading-none">{l.label}</span>
                  <span className="block text-[0.7rem] tracking-[0.18em] uppercase mt-1 text-[#737554] group-hover:text-[#F7F4EB]/80">
                    {l.sub}
                  </span>
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1 text-lg">→</span>
              </Link>
            ) : (
              <a
                key={i}
                href={l.href}
                target={l.href.startsWith("http") ? "_blank" : undefined}
                rel={l.href.startsWith("http") ? "noreferrer" : undefined}
                data-testid={`link-${i}`}
                className="group flex items-center gap-4 bg-[#F7F4EB] hover:bg-[#BC472A] text-[#2C2C2A] hover:text-[#F7F4EB] px-5 py-4 rounded-xl border border-[#F7F4EB]/10 transition-all duration-300 hover:-translate-y-0.5 shadow-md"
              >
                <span className="shrink-0 h-10 w-10 grid place-items-center rounded-lg bg-[#2C2C2A]/8 group-hover:bg-[#F7F4EB]/15 text-[#BC472A] group-hover:text-[#F7F4EB] transition-colors">
                  {l.icon}
                </span>
                <span className="flex-1 text-left">
                  <span className="block font-serif text-lg leading-none">{l.label}</span>
                  <span className="block text-[0.7rem] tracking-[0.18em] uppercase mt-1 text-[#737554] group-hover:text-[#F7F4EB]/80">
                    {l.sub}
                  </span>
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1 text-lg">
                  {l.href.startsWith("http") ? "↗" : "→"}
                </span>
              </a>
            )
          )}
        </div>

        {/* Footer mini */}
        <p className="mt-12 text-[0.66rem] tracking-[0.3em] uppercase text-[#EDE6D6]/50 text-center">
          Via A. Gramsci 55 · 57016 Rosignano M.mo
        </p>
      </div>
    </div>
  );
}
